import { TradingData } from "../helpers/CSVParser";

export interface Metrics {
    [key: string]: number | string; // Adjust the type according to your actual data structure
}

export interface OverallMetricsWithTitle {
    title: string;
    metrics: OverallTradingMetrics;
    tradingData: TradingData[];
}

export interface MaxDrawdown {
  maxDrawdown: number;
  maxDrawdownDate: number;
  asPercentOfInitialEquity: number;
  asPercentOfTotalEquity: number;
}

export interface MaxRunup {
  maxRunup: number;
  maxRunupDate: number;
  asPercentOfInitialEquity: number;
  asPercentOfTotalEquity: number;
}

export interface TradingPeriod {
  start: number;
  end: number;
  tradingDays: number;
}

export interface AvgWorst5DD {
  avgWorst5DD: number;
  asPercentOfInitialEquity: number;
}

export interface OverallTradingMetrics {
  totalNetProfit: number;
  grossProfit: number;
  grossLoss: number;
  profitFactor: number;
  maxDrawdown: MaxDrawdown;
  pessimisticRR: number;
  avgTradeNetProfit: number;
  avgWinningTrade: number;
  avgLosingTrade: number;
  winningTradesCount: number;
  losingTradesCount: number;
  totalTrades: number;
  percentProfitable: number;
  largestWin: number;
  largestLoss: number;
  maxConsWinners: number;
  maxConsLosers: number;
  annualRateOfReturn: number;
  sharpeRatio: number;
  marginRequirements: number;
  maxIntradayDrawdown: MaxDrawdown;
  longestDrawdownDays: number;
  recoveryFactor: number;
  npMaxDD: number;
  npIntradayMaxDD: number;
  avgWorst5DD: AvgWorst5DD;
  maxRunup: MaxRunup;
  longestRunupDays: number;
  longestFlatPeriodDays: number;
  returnOnInitialCapital: number;
  avgMonthlyReturn: number;
  stdDevMonthlyReturn: number;
  percentProfitableMonths: number;
  tradingPeriod: TradingPeriod;
  initialCapital: number;
}

export const initialOverallTradingMetrics: OverallTradingMetrics = {
  totalNetProfit: 0,
  grossProfit: 0,
  grossLoss: 0,
  profitFactor: 0,
  maxDrawdown: { maxDrawdown: 0, maxDrawdownDate: 0, asPercentOfInitialEquity: 0, asPercentOfTotalEquity: 0 },
  pessimisticRR: 0,
  avgTradeNetProfit: 0,
  avgWinningTrade: 0,
  avgLosingTrade: 0,
  winningTradesCount: 0,
  losingTradesCount: 0,
  totalTrades: 0,
  percentProfitable: 0,
  largestWin: 0,
  largestLoss: 0,
  maxConsWinners: 0,
  maxConsLosers: 0,
  annualRateOfReturn: 0,
  sharpeRatio: 0,
  marginRequirements: 0,
  maxIntradayDrawdown: { maxDrawdown: 0, maxDrawdownDate: 0, asPercentOfInitialEquity: 0, asPercentOfTotalEquity: 0 },
  longestDrawdownDays: 0,
  recoveryFactor: 0,
  npMaxDD: 0,
  npIntradayMaxDD: 0,
  avgWorst5DD: { avgWorst5DD: 0, asPercentOfInitialEquity: 0 },
  maxRunup: { maxRunup: 0, maxRunupDate: 0, asPercentOfInitialEquity: 0, asPercentOfTotalEquity: 0 },
  longestRunupDays: 0,
  longestFlatPeriodDays: 0,
  returnOnInitialCapital: 0,
  avgMonthlyReturn: 0,
  stdDevMonthlyReturn: 0,
  percentProfitableMonths: 0,
  tradingPeriod: { start: 0, end: 0, tradingDays: 0 },
  initialCapital: 0
};

export interface YearlyTradingMetrics {
  period: number;
  netProfit: number;
  returnOnIE: number;
  returnOnTE: number;
  trades: number;
  profitFactor: number;
  percentProfitable: number;
}

export interface MonthlyTradingMetrics {
  period: string;
  netProfit: number;
  netProfitIfPositive: number;
  netProfitIfNegative: number;
  totalProfits: number;
  totalLosses: number;
  returnOnIE: number;
  returnOnTE: number;
  trades: number;
  profitFactor: number;
  percentProfitable: number;
}

export interface DailyTradingMetrics {
  period: string;
  netProfit: number;
  returnOnIE: number;
  returnOnTE: number;
  trades: number;
  profitFactor: number;
  percentProfitable: number;
}

export interface WeeklyTradingMetrics {
  period: string;
  netProfit: number;
  returnOnIE: number;
  returnOnTE: number;
  trades: number;
  profitFactor: number;
  percentProfitable: number;
}
